import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { useCompetitions } from '@/hooks/useCompetitions';
import { useTeams } from '@/hooks/useTeams';
import { useUser } from '@/hooks/useUser';
import { useAuth } from '@/contexts/AuthContext';
import { initiateOAuthLogin } from '@/utils/oauth';
import { useTenant } from '@/contexts/TenantContext';
import { usePickemById } from '@/api/query/usePickem';
import { transformPickemToCompetition } from '@/utils/type-transformers';
import type { Competition } from '@/types/domain';
import { getLeagueLogoUrl } from '@/utils/pickem-helpers';
import { generateAdDivId } from '@/config/ads.config';
import { resolveLobbyReturnUrl } from '@low6-pickem/frontend-shared';

// Components
import AppHeader from '@/components/layout/AppHeader';
import { AppHeaderStyleProvider } from '@low6-pickem/ui';
import { useDefaultHeaderStyle } from '@/hooks/useDefaultHeaderStyle';
import AppFooter from '@/components/layout/AppFooter';
import NavigationTabs from '@/components/layout/NavigationTabs';
import { ScreenLayout } from '@/components/layout/ScreenLayout';
import GameCard from '@/components/game/GameCard';
import GameCardSkeleton from '@/components/game/GameCardSkeleton';
import PickemLeaderboard from '@/components/leaderboard/PickemLeaderboard';
import LeaderboardSection from '@/components/leaderboard/LeaderboardSection';
import { CompetitionView } from '@/components/screens/CompetitionView';
import LeagueLeaderboardView from '@/components/leaderboard/LeagueLeaderboardView';
import LeagueCard from '@/components/leaderboard/LeagueCard';
import EmptyState from '@/components/shared/EmptyState';
import SportFilter from '@/components/shared/SportFilter';
import type { SportFilterValue } from '@/components/shared/SportFilter';
import WelcomeScreen from '@/components/screens/WelcomeScreen';
import HowToPlayScreen from '@/components/screens/HowToPlayScreen';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { ErrorBoundary } from '@/components/shared/ErrorBoundary';
import AdSlot from '@/components/ads/AdSlot';
import TrackingAd from '@/components/ads/TrackingAd';
import { TopPageAd, BelowHeaderAd } from '@/components/ads/HeaderAds';
import {
  LeagueCreatedModal,
  PicksSubmittedModal,
  ClearSelectionsModal,
} from '@/components/modals/AppModal';
import ContestDetailsModal from '@/components/modals/ContestDetailsModal';

const ENABLE_MOBILE_CONTAINER = false;

/**
 * Counts the number of distinct underlying fixtures (games) a competition covers.
 * Prefer the server-computed `gamesCount` (single source of truth, matches the pickem
 * detail screen). This client-side count is only a fallback for older payloads that
 * predate `games_count`: de-duplicate by fixtureId, falling back to the question count
 * when fixture ids are unavailable to avoid showing 0.
 */
const countGames = (competition: Competition): number => {
  if (typeof competition.gamesCount === 'number') return competition.gamesCount;
  const fixtureIds = new Set(
    competition.questions.map(q => q.fixtureId).filter((id): id is string => !!id)
  );
  return fixtureIds.size > 0 ? fixtureIds.size : competition.questions.length;
};

const PickEmApp: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();

  const [_selectedTeams, setSelectedTeams] = useState<Record<string, string>>({});
  const [_selectedProps, setSelectedProps] = useState<Record<string, 'yes' | 'no'>>({});

  // Modal states
  const [showLeagueCreated, setShowLeagueCreated] = useState(false);
  const [showPicksSubmitted, setShowPicksSubmitted] = useState(false);
  const [showClearSelections, setShowClearSelections] = useState(false);
  const [contestDetailsGameId, setContestDetailsGameId] = useState<string | null>(null);

  // Sport filter state (persists across tab switches)
  const [selectedSport, setSelectedSport] = useState<SportFilterValue>('ALL');

  // Get data from hooks
  const { competitions, isLoading, isFetched } = useCompetitions();
  // Landing/lobby header styling, sourced from the tenant's default-style pickem
  // so the configured banner shows here too (not just on the detail screen) — DKPLAT-446.
  const defaultHeaderStyle = useDefaultHeaderStyle();
  const { getTeamPair: _getTeamPair } = useTeams();
  const { currentUser: _currentUser } = useUser();
  const { isAuthenticated, isLoading: _isAuthLoading } = useAuth();
  const {
    tenant,
    gamConfig,
    isAdsEnabled,
    adPlacements,
    isPlacementEnabled,
    pickemFullNavigation,
  } = useTenant();
  const showAdPlaceholders = import.meta.env.MODE === 'development';

  // Fetch pickem details for contest details modal
  const { data: contestDetailsData } = usePickemById(contestDetailsGameId || '');
  // Transform pickem data to competition format
  // Get totalEntries from competitions array (list endpoint has entries, detail endpoint doesn't)
  const contestDetailsCompetition = useMemo(() => {
    if (!contestDetailsData?.pickem) return undefined;
    const transformed = transformPickemToCompetition(contestDetailsData.pickem);
    // Add totalEntries from competitions array if not already set
    if (transformed.totalEntries === undefined && contestDetailsGameId) {
      const comp = competitions.find(c => c.id === contestDetailsGameId);
      if (comp?.totalEntries !== undefined) {
        transformed.totalEntries = comp.totalEntries;
      }
    }
    return transformed;
  }, [contestDetailsData, contestDetailsGameId, competitions]);

  // TOP_PAGE / BELOW_HEADER ad placements are rendered via <TopPageAd /> and
  // <BelowHeaderAd /> wherever AppHeader is shown (here + CompetitionView +
  // HowToPlayScreen); those components own their own gating.
  const showBodyAds =
    !!adPlacements && isPlacementEnabled('BODY') && (isAdsEnabled || showAdPlaceholders);

  // Check if user has seen welcome screen
  const hasSeenWelcome = localStorage.getItem('hasSeenWelcome') === 'true';

  // Determine current screen from URL
  const currentScreen = useMemo(() => {
    const path = location.pathname;
    if (path === '/welcome') return pickemFullNavigation ? 'main' : 'welcome';
    if (path === '/how-to-play') return pickemFullNavigation ? 'main' : 'howToPlay';
    if (path.startsWith('/game/')) return 'competition';
    if (path.startsWith('/league/')) return 'leagueLeaderboard';
    if (path.startsWith('/main') || path === '/') {
      // Show welcome screen on first load if user hasn't seen it (skip entirely
      // for full-navigation tenants — DKPLAT-652 — they land straight on Lobby)
      if (!hasSeenWelcome && path === '/' && !pickemFullNavigation) {
        return 'welcome';
      }
      return 'main';
    }
    return pickemFullNavigation ? 'main' : hasSeenWelcome ? 'main' : 'welcome';
  }, [location.pathname, hasSeenWelcome, pickemFullNavigation]);

  // Proactively send unauthenticated visitors to OAuth instead of waiting for a
  // background query to 401 (DKPLAT-462). By the time PickEmApp renders, AuthContext
  // has resolved (App.tsx blocks on its isLoading), so !isAuthenticated here is
  // authoritative — the user has no token. Doing the redirect here means the welcome
  // path's spinner is a real "redirecting to login" state, not a dead end if no
  // request happens to 401 (e.g. a network error). Guarded so it fires once.
  const oauthInitiatedRef = useRef(false);
  const [loginRedirectFailed, setLoginRedirectFailed] = useState(false);

  // Kick off (or retry) the OAuth redirect. A successful redirect navigates the
  // page away; a failure (backend/OAuth error) rejects here, so we surface a retry
  // screen instead of leaving the user on an endless spinner.
  const startLoginRedirect = useCallback(() => {
    oauthInitiatedRef.current = true;
    setLoginRedirectFailed(false);
    initiateOAuthLogin().catch(() => {
      oauthInitiatedRef.current = false;
      setLoginRedirectFailed(true);
    });
  }, []);

  useEffect(() => {
    if (isAuthenticated || oauthInitiatedRef.current) return;
    startLoginRedirect();
    // Backstop: a working redirect unloads this page, so if we're still mounted
    // after a generous grace period the navigation silently stalled — show the
    // retry screen rather than spinning forever. Long enough not to false-fire on
    // slow links (e.g. Delta in-flight).
    const timer = setTimeout(() => setLoginRedirectFailed(true), 20000);
    return () => clearTimeout(timer);
  }, [isAuthenticated, startLoginRedirect]);

  // Get active tab from URL params or default to 'myPicks'. Full-navigation
  // tenants (pre-DKPLAT-347) land on the Lobby tab by default.
  const activeTab =
    params.tab || searchParams.get('tab') || (pickemFullNavigation ? 'lobby' : 'myPicks');

  // Smart landing for the root URL: send the user straight into a pickem
  // when they have no entries yet, or to My Picks when they already do.
  // Only fires for path === '/' once competitions have loaded — leaves
  // explicit URLs (e.g. /main/myPicks) alone.
  useEffect(() => {
    // Gate on isFetched (not !isLoading) — `isLoading` is also false while
    // the underlying query is *disabled* waiting for user context, which
    // would cause us to redirect to My Picks before the data ever arrives.
    //
    // Skip entirely for full-navigation tenants (pre-DKPLAT-347): they land on
    // the Lobby tab and navigate manually rather than being auto-routed.
    if (pickemFullNavigation) return;
    if (location.pathname !== '/' || !hasSeenWelcome || !isFetched) return;

    const playable = competitions
      .filter(c => c.state !== 'final')
      .sort((a, b) => {
        if (a.isOpen !== b.isOpen) return a.isOpen ? -1 : 1;
        return a.gameDate.getTime() - b.gameDate.getTime();
      });

    const next = playable[0];
    // A reservation counts as a real entry (DKPLAT-528), so a user who's
    // already reserved a seat (but hasn't made picks, e.g. questions aren't
    // posted yet) should land on My Picks like any other entered contest,
    // not be routed back into the entry screen.
    if (next && !next.hasSelections && !next.hasReservation) {
      navigate(`/game/${next.id}`, { replace: true });
    } else {
      navigate('/main/myPicks', { replace: true });
    }
  }, [location.pathname, hasSeenWelcome, isFetched, competitions, navigate, pickemFullNavigation]);

  // Get game ID and league info from URL params
  const currentGameId = params.gameId || '';
  const currentLeague = useMemo(() => {
    const leagueName = params.leagueName || '';
    const color = (searchParams.get('color') as 'orange' | 'blue') || 'orange';
    return { name: decodeURIComponent(leagueName), color };
  }, [params.leagueName, searchParams]);

  const handleCreateLeague = () => {
    setShowLeagueCreated(true);
  };

  const handleClearSelections = () => {
    setSelectedTeams({});
    setSelectedProps({});
    setShowClearSelections(false);
  };

  const handleGameAction = (gameId: string) => {
    navigate(`/game/${gameId}?from=${activeTab}`);
  };

  /**
   * "Edit Picks" on a contest card. `edit=1` opens CompetitionView on the selections
   * themselves — pressing Edit Picks should never land on a summary whose only content
   * is another Edit Picks button.
   */
  const handleEditPicks = (gameId: string) => {
    navigate(`/game/${gameId}?from=${activeTab}&edit=1`);
  };

  const handleViewLeaderboard = (gameId: string) => {
    navigate(`/game/${gameId}?mode=leaderboard&from=${activeTab}`);
  };

  const handleLeagueLeaderboard = (leagueName: string, leagueColor: 'orange' | 'blue') => {
    navigate(`/league/${encodeURIComponent(leagueName)}?color=${leagueColor}`);
  };

  const handleTabChange = (tab: string) => {
    navigate(`/main/${tab}`);
  };

  if (currentScreen === 'leagueLeaderboard') {
    return (
      <ScreenLayout enableMobileContainer={ENABLE_MOBILE_CONTAINER}>
        <LeagueLeaderboardView
          leagueName={currentLeague.name}
          leagueColor={currentLeague.color}
          onBack={() => navigate('/main/friends')}
        />
      </ScreenLayout>
    );
  }

  if (currentScreen === 'competition') {
    return (
      <ScreenLayout enableMobileContainer={ENABLE_MOBILE_CONTAINER}>
        <ErrorBoundary resetKeys={[currentGameId]}>
          {/* Full-navigation tenants (e.g. SkyPicks) navigate entirely in-app, so
              back always returns to the originating in-app tab (Lobby by default).
              This must take priority: the in-app Lobby tab opens contests with
              ?from=lobby (from=${activeTab}), which would otherwise collide with the
              external-lobby branch below and bounce them to the canonical external
              /tenant/{code}/lobby URL they don't use (DKPLAT-461).
              Non-full-nav (DK): when the user came from the external Lobby front-end
              (?from=lobby), return there; otherwise step back in history. */}
          <CompetitionView
            gameId={currentGameId}
            onBack={
              pickemFullNavigation
                ? () => navigate(`/main/${searchParams.get('from') || 'lobby'}`)
                : searchParams.get('from') === 'lobby'
                  ? () => {
                      window.location.href = resolveLobbyReturnUrl(
                        searchParams,
                        'pickem_lobby_return_url'
                      );
                    }
                  : () => navigate(-1)
            }
          />
        </ErrorBoundary>
      </ScreenLayout>
    );
  }

  // Screen routing
  if (currentScreen === 'welcome') {
    // Don't flash the Welcome CTAs to a user who isn't authenticated yet — the effect
    // above redirects them to OAuth (apiClient's 401 interceptor in api/client.ts is a
    // secondary backstop). Show a loading indicator until auth resolves (DKPLAT-462).
    // Affects SkyPicks (iframe OAuth, no URL token on first load); DK passes the SSO
    // token in the URL so it's authenticated on first paint and is unaffected.
    if (!isAuthenticated) {
      return (
        <ScreenLayout enableMobileContainer={ENABLE_MOBILE_CONTAINER}>
          <LoadingSpinner className="min-h-screen" />
        </ScreenLayout>
      );
    }
    return (
      <ScreenLayout enableMobileContainer={ENABLE_MOBILE_CONTAINER}>
        <WelcomeScreen
          onHowToPlay={() => navigate('/how-to-play')}
          onLetsPlay={() => {
            localStorage.setItem('hasSeenWelcome', 'true');
            navigate(pickemFullNavigation ? '/main/lobby' : '/main/myPicks');
          }}
        />
      </ScreenLayout>
    );
  }

  if (currentScreen === 'howToPlay') {
    return (
      <ScreenLayout enableMobileContainer={ENABLE_MOBILE_CONTAINER}>
        <HowToPlayScreen
          onBack={() => navigate('/welcome')}
          onGotIt={() => {
            localStorage.setItem('hasSeenWelcome', 'true');
            navigate(pickemFullNavigation ? '/main/lobby' : '/main/myPicks');
          }}
        />
      </ScreenLayout>
    );
  }

  // SkyPicks (iframe OAuth, no URL token on first load) is briefly unauthenticated
  // on first paint, before the effect above redirects to OAuth. Show the spinner
  // instead of flashing the Lobby / main tabs. DK passes the SSO token in the URL,
  // so it's authenticated on first paint and never hits this branch — the normal
  // DK pickem is unaffected. Extends the welcome-screen guard (DKPLAT-462) to the
  // default tabbed view, which is what `/skypicks/entry` lands on (DKPLAT-662).
  // If the login redirect can't be reached, fall back to a retry screen rather
  // than an endless spinner (DKPLAT-662).
  if (!isAuthenticated) {
    return (
      <ScreenLayout enableMobileContainer={ENABLE_MOBILE_CONTAINER}>
        {loginRedirectFailed ? (
          <EmptyState
            className="min-h-screen"
            title={t('auth.loginUnavailableTitle')}
            subtitle={t('auth.loginUnavailableSubtitle')}
            actionLabel={t('auth.retry')}
            onAction={startLoginRedirect}
          />
        ) : (
          <LoadingSpinner className="min-h-screen" />
        )}
      </ScreenLayout>
    );
  }

  // Filter competitions by selected sport
  const filterBySport = (comps: typeof competitions) => {
    if (selectedSport === 'ALL') return comps;
    return comps.filter(c => c.sport?.toUpperCase() === selectedSport);
  };

  // Show filter on lobby, myPicks, and recent tabs
  const showSportFilter =
    activeTab === 'lobby' || activeTab === 'myPicks' || activeTab === 'recent';

  // Main app content
  const renderContent = () => {
    switch (activeTab) {
      case 'lobby': {
        if (isLoading) {
          return (
            <div className="space-y-6">
              {[...Array(5)].map((_, index) => (
                <motion.div
                  key={`skeleton-${index}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05, duration: 0.3 }}
                >
                  <GameCardSkeleton />
                </motion.div>
              ))}
            </div>
          );
        }
        // Show all non-final contests on the lobby — including ones the user has
        // already entered. Entered contests also appear on My Picks; they should
        // remain visible on the lobby too, not be removed once entered (DKPLAT-449).
        const activeGames = filterBySport(competitions)
          .filter(g => g.state !== 'final')
          .sort((a, b) => {
            // Sort by isOpen: open games first (true before false)
            if (a.isOpen !== b.isOpen) {
              return a.isOpen ? -1 : 1;
            }
            // Then sort by date (soonest first)
            return a.gameDate.getTime() - b.gameDate.getTime();
          });

        // Invisible 1x1 GAM impression pixel fired once per lobby render so the
        // business can measure lobby visitors (not just contest entries). Pure
        // no-op while the tenant's gamEnabled flag is off (gamConfig is null).
        return (
          <TrackingAd
            id="lobby"
            enabled={gamConfig?.enabled ?? false}
            networkId={gamConfig?.networkId ?? ''}
            targeting={{
              s1: 'free-to-play',
              s2: tenant?.code ?? '',
              s3: 'lobby',
            }}
          >
            {activeGames.length > 0 ? (
              <div className="space-y-6">
                <AnimatePresence mode="popLayout">
                  {activeGames.map((game, index) => (
                    <React.Fragment key={`game-${game.id}`}>
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ delay: index * 0.05, duration: 0.3 }}
                      >
                        <GameCard
                          id={game.id}
                          title={game.title}
                          prize={game.prize}
                          state={game.state}
                          gameDate={game.gameDate}
                          hasSelections={game.hasSelections}
                          hasReservation={game.hasReservation}
                          hasQuestions={game.hasQuestions}
                          questionsPostedAt={game.questionsPostedAt}
                          isLocked={!game.isOpen}
                          isOpen={game.isOpen}
                          currentPoints={game.currentPoints}
                          totalPicks={countGames(game)}
                          totalEntries={game.totalEntries}
                          entryCost="FREE"
                          leagueLogoUrl={getLeagueLogoUrl(game.sport)}
                          onEdit={() => handleEditPicks(game.id)}
                          onPlay={() => handleGameAction(game.id)}
                          onView={() => handleGameAction(game.id)}
                          onViewLeaderboard={() => handleViewLeaderboard(game.id)}
                          onMenuClick={() => setContestDetailsGameId(game.id)}
                        />
                      </motion.div>

                      {/* Insert ad after the 3rd contest */}
                      {showBodyAds && index === 2 && (
                        <motion.div
                          key="ad-0"
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -20 }}
                          transition={{ delay: (index + 1) * 0.05, duration: 0.3 }}
                          className="my-6"
                        >
                          <AdSlot
                            divId={generateAdDivId(adPlacements, 'BODY', true, 0)}
                            adPlacement={adPlacements.BODY}
                            placement="lobby-after-contest-3"
                            className="rounded-lg overflow-hidden"
                          />
                        </motion.div>
                      )}
                    </React.Fragment>
                  ))}
                </AnimatePresence>
              </div>
            ) : (
              <EmptyState title={t('pages.noGamesAvailable')} showAdSpace={false} />
            )}
          </TrackingAd>
        );
      }

      case 'myPicks': {
        if (isLoading) {
          return (
            <div className="space-y-6">
              {[...Array(3)].map((_, index) => (
                <motion.div
                  key={`skeleton-${index}`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <GameCardSkeleton />
                </motion.div>
              ))}
            </div>
          );
        }

        // Only show active picks (not completed/final games - those go to Recent tab).
        // A reservation counts as a real entry (DKPLAT-528), so reserved-but-not-yet-
        // picked contests show up here too.
        const myPickGames = filterBySport(competitions).filter(
          g => (g.hasSelections || g.hasReservation) && g.state !== 'final'
        );

        return myPickGames.length > 0 ? (
          <div className="space-y-6">
            <AnimatePresence mode="popLayout">
              {myPickGames.map((game, index) => (
                <React.Fragment key={`game-${game.id}`}>
                  <motion.div
                    key={game.id}
                    layout
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                  >
                    <GameCard
                      id={game.id}
                      title={game.title}
                      prize={game.prize}
                      state={game.state}
                      gameDate={game.gameDate}
                      hasSelections={game.hasSelections}
                      hasReservation={game.hasReservation}
                      hasQuestions={game.hasQuestions}
                      questionsPostedAt={game.questionsPostedAt}
                      isLocked={!game.isOpen}
                      isOpen={game.isOpen}
                      currentPoints={game.currentPoints}
                      totalPicks={countGames(game)}
                      correctPicks={game.correctPicks}
                      finalScore={game.finalScore}
                      totalEntries={game.totalEntries}
                      entryCost="FREE"
                      leagueLogoUrl={getLeagueLogoUrl(game.sport)}
                      onEdit={() => handleEditPicks(game.id)}
                      onView={() => handleGameAction(game.id)}
                      onViewLeaderboard={() => handleViewLeaderboard(game.id)}
                      onMenuClick={() => setContestDetailsGameId(game.id)}
                    />
                  </motion.div>

                  {/* Insert ad after the 3rd contest */}
                  {showBodyAds && index === 2 && (
                    <motion.div
                      key="ad-0"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ delay: (index + 1) * 0.05, duration: 0.3 }}
                      className="my-6"
                    >
                      <AdSlot
                        divId={generateAdDivId(adPlacements, 'BODY', true, 0)}
                        adPlacement={adPlacements.BODY}
                        placement="lobby-after-contest-3"
                        className="rounded-lg overflow-hidden"
                      />
                    </motion.div>
                  )}
                </React.Fragment>
              ))}
            </AnimatePresence>
          </div>
        ) : (
          <EmptyState
            title="No picks have been made from the upcoming games."
            subtitle=""
            showAdSpace={false}
            showIcon={false}
          />
        );
      }

      case 'recent': {
        if (isLoading) {
          return (
            <div className="space-y-6">
              {[...Array(3)].map((_, index) => (
                <motion.div
                  key={`skeleton-${index}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05, duration: 0.3 }}
                >
                  <GameCardSkeleton />
                </motion.div>
              ))}
            </div>
          );
        }
        // Filter for completed games within the last 7 days
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        const recentGames = filterBySport(competitions).filter(
          g => g.state === 'final' && g.gameDate >= sevenDaysAgo
        );
        return recentGames.length > 0 ? (
          <div className="space-y-6">
            <AnimatePresence mode="popLayout">
              {recentGames.map((game, index) => (
                <React.Fragment key={`game-${game.id}`}>
                  <motion.div
                    key={game.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ delay: index * 0.05, duration: 0.3 }}
                  >
                    <GameCard
                      id={game.id}
                      title={game.title}
                      prize={game.prize}
                      state={game.state}
                      gameDate={game.gameDate}
                      finalScore={game.finalScore}
                      hasSelections={game.hasSelections}
                      isOpen={game.isOpen}
                      totalPicks={countGames(game)}
                      correctPicks={game.correctPicks}
                      totalEntries={game.totalEntries}
                      entryCost="FREE"
                      leagueLogoUrl={getLeagueLogoUrl(game.sport)}
                      onView={() => handleGameAction(game.id)}
                      onViewLeaderboard={() => handleViewLeaderboard(game.id)}
                      onMenuClick={() => setContestDetailsGameId(game.id)}
                    />
                  </motion.div>

                  {/* Insert ad after the 3rd contest */}
                  {showBodyAds && index === 2 && (
                    <motion.div
                      key="ad-0"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ delay: (index + 1) * 0.05, duration: 0.3 }}
                      className="my-6"
                    >
                      <AdSlot
                        divId={generateAdDivId(adPlacements, 'BODY', true, 0)}
                        adPlacement={adPlacements.BODY}
                        placement="lobby-after-contest-3"
                        className="rounded-lg overflow-hidden"
                      />
                    </motion.div>
                  )}
                </React.Fragment>
              ))}
            </AnimatePresence>
          </div>
        ) : (
          <EmptyState title={t('pages.noCompletedGames')} showAdSpace={false} />
        );
      }

      case 'leaderboard': {
        if (isLoading) {
          return <LoadingSpinner message="Loading leaderboard..." />;
        }
        // Full-navigation tenants (pre-DKPLAT-347) show the aggregate global
        // leaderboard rather than the single most-relevant pickem board.
        if (pickemFullNavigation) {
          return (
            <div className="space-y-6">
              <LeaderboardSection title="Global Leaderboard" />
            </div>
          );
        }
        // Show the leaderboard for the most relevant active pickem (open
        // for picks first, then soonest upcoming). Falls back to a placeholder
        // when nothing is playable.
        const playable = competitions
          .filter(c => c.state !== 'final')
          .sort((a, b) => {
            if (a.isOpen !== b.isOpen) return a.isOpen ? -1 : 1;
            return a.gameDate.getTime() - b.gameDate.getTime();
          });
        const activePickem = playable[0];
        if (!activePickem) {
          return <EmptyState title="No active leaderboard" showAdSpace={false} />;
        }
        return (
          <PickemLeaderboard
            pickemId={activePickem.id}
            isOpen={true}
            onClose={() => {}}
            currentUserId={_currentUser?.id}
            contestStartUtc={activePickem.gameDate.toISOString()}
          />
        );
      }

      case 'friends':
        return (
          <div className="space-y-6">
            {/* League Cards */}
            <div className="space-y-4">
              <LeagueCard
                name="DA BOYZ ARE BACK"
                color="orange"
                onViewLeaderboard={() => handleLeagueLeaderboard('DA BOYZ ARE BACK', 'orange')}
                onSettings={() => console.log('Orange settings')}
              />

              <LeagueCard
                name="WORK FRIENDS"
                color="blue"
                onViewLeaderboard={() => handleLeagueLeaderboard('WORK FRIENDS', 'blue')}
                onSettings={() => console.log('Blue settings')}
              />
            </div>

            {/* Challenge Friends Section */}
            <div className="bg-game-card rounded-xl p-6 text-center">
              <div className="flex justify-center items-center gap-4 mb-4">
                <span className="text-2xl">😄</span>
                <h3 className="text-text-primary font-bold text-xl">CHALLENGE YOUR FRIENDS</h3>
                <span className="text-2xl">😎</span>
              </div>

              <p className="text-text-secondary mb-6">
                Create a private leaderboard for you and your friends!
              </p>

              <div className="space-y-3">
                <div className="flex gap-3">
                  <Button variant="secondary" className="flex-1">
                    Join a League
                  </Button>
                  <Button variant="pickem" onClick={handleCreateLeague} className="flex-1">
                    Create New League
                  </Button>
                </div>

                <p className="text-text-muted text-sm">
                  Received an invite? Join using your PIN you received
                </p>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <AppHeaderStyleProvider
      value={{
        headerBgGradientStart: defaultHeaderStyle.headerBgGradientStart,
        headerBgGradientEnd: defaultHeaderStyle.headerBgGradientEnd,
        headerBgImageUrl: defaultHeaderStyle.headerBgImageUrl,
        headerBgImageUrlMobile: defaultHeaderStyle.headerBgImageUrlMobile,
      }}
    >
      <ScreenLayout enableMobileContainer={ENABLE_MOBILE_CONTAINER}>
        <div
          className={
            ENABLE_MOBILE_CONTAINER ? 'h-full overflow-y-auto' : 'min-h-screen flex flex-col'
          }
        >
          {/* Top-of-page ad placement */}
          <TopPageAd />

          {defaultHeaderStyle.isHeaderVisible && (
            <>
              <AppHeader
                headerLogoUrl={defaultHeaderStyle.headerLogoUrl}
                isLogoLoading={defaultHeaderStyle.isLoading}
                fullNavigation={pickemFullNavigation}
                suppressFallbacks
              />

              {/* Below-header ad placement */}
              <BelowHeaderAd />
            </>
          )}

          <main className="px-4 pb-6 flex-1">
            <NavigationTabs
              activeTab={activeTab}
              onTabChange={handleTabChange}
              fullNavigation={pickemFullNavigation}
            />

            {showSportFilter && (
              <SportFilter selectedSport={selectedSport} onSportChange={setSelectedSport} />
            )}

            <div key={activeTab}>{renderContent()}</div>
          </main>

          <AppFooter />
        </div>
      </ScreenLayout>

      {/* Other Modals */}
      <LeagueCreatedModal
        isOpen={showLeagueCreated}
        onClose={() => setShowLeagueCreated(false)}
        pin="8JK376"
        onViewLeague={() => {
          setShowLeagueCreated(false);
          navigate('/main/friends');
        }}
      />

      <PicksSubmittedModal
        isOpen={showPicksSubmitted}
        onClose={() => setShowPicksSubmitted(false)}
        potentialWinnings="$813.95"
        onAddToBetSlip={() => setShowPicksSubmitted(false)}
        onReturnToLobby={() => setShowPicksSubmitted(false)}
      />

      <ClearSelectionsModal
        isOpen={showClearSelections}
        onClose={() => setShowClearSelections(false)}
        onClearPicks={handleClearSelections}
      />

      {/* Contest Details Modal */}
      <ContestDetailsModal
        isOpen={!!contestDetailsGameId}
        onClose={() => setContestDetailsGameId(null)}
        pickemDetails={contestDetailsData}
        competitionData={contestDetailsCompetition}
      />
    </AppHeaderStyleProvider>
  );
};

export default PickEmApp;
